// Users tab — who this project has seen. Identities belong to the office
// (users are provisioned just-in-time on their first embed token), so the
// table is read-only: name, e-mail, external id, kind, first seen, last login.

import { useTranslation } from 'react-i18next';
import { Loader2, Search as SearchIcon, Users } from 'lucide-react';
import moment from 'moment';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ep } from '../lib/api.js';
import { usePagedList } from '../lib/usePagedList.js';
import { ListPagination } from './ListPagination.jsx';

function fmt(value) {
  return value ? moment(value).format('DD MMM YYYY HH:mm') : '—';
}

export const UsersTab = ({ projectId }) => {
  const { t } = useTranslation('console');
  const list = usePagedList(ep.users(projectId), { size: 25 });
  const items = list.items;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">{t('users.intro')}</p>
        <div className="relative flex-1 min-w-56 max-w-md">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={list.searchInput} onChange={(e) => list.setSearchInput(e.target.value)} placeholder={t('users.searchPlaceholder')} className="pl-9" />
        </div>
      </div>

      {list.loading && items.length === 0 ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 text-blue-500 dark:text-blue-400 animate-spin" /></div>
      ) : items.length > 0 ? (
        <>
          <div className="w-full border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('users.table.name')}</TableHead>
                  <TableHead>{t('users.table.email')}</TableHead>
                  <TableHead>{t('users.table.externalId')}</TableHead>
                  <TableHead className="w-24">{t('users.table.type')}</TableHead>
                  <TableHead className="w-40 text-center">{t('users.table.created')}</TableHead>
                  <TableHead className="w-40 text-center">{t('users.table.lastLogin')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium">
                      {u.display_name || <span className="text-muted-foreground">#{u.id}</span>}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{u.email || '—'}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground max-w-48 truncate" title={u.external_user_id || ''}>{u.external_user_id || '—'}</TableCell>
                    <TableCell>
                      <span className="inline-flex rounded-full border bg-muted/50 px-2 py-0.5 text-xs">{t(`users.types.${u.user_type}`, { defaultValue: u.user_type })}</span>
                    </TableCell>
                    <TableCell className="text-center text-sm text-muted-foreground">{fmt(u.created_at)}</TableCell>
                    <TableCell className="text-center text-sm text-muted-foreground" title={u.last_login_at ? moment(u.last_login_at).fromNow() : undefined}>
                      {u.last_login_at ? fmt(u.last_login_at) : t('users.neverLoggedIn')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <ListPagination list={list} />
        </>
      ) : (
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
          <Users className="h-12 w-12 mb-3 text-muted-foreground/60" />
          <p className="text-lg font-medium">{list.hasFilters ? t('list.noMatch') : t('users.empty.title')}</p>
          <p className="text-sm text-muted-foreground">{list.hasFilters ? t('list.noMatchHint') : t('users.empty.description')}</p>
          {list.hasFilters && <Button variant="ghost" size="sm" className="mt-2" onClick={list.clearFilters}>{t('list.clearFilters')}</Button>}
        </div>
      )}
    </div>
  );
};
